import { joiResolver } from '@hookform/resolvers/joi'
import { Box } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'
import { getInstallationRequests } from '../../installation/api/save-installation/get-installation-requests'
import { InstallationFormModel } from '../../installation/forms'
import { getTreatHtcAsNonSgReady } from '../../installation/utils'
import { InstallationFormRoutes } from './InstallationFormRoutes'
//TODO: change joi to zod
import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useModals from 'src/Hooks/useModals'
import { getValidationMessages, installationSchema } from '../validations'
import { InstallationFormFooter } from './Header'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'

interface InstallationFormProps {
  parentBreadcrumb: Breadcrumb
  defaultValues: InstallationFormModel
}

export const InstallationForm: React.FC<InstallationFormProps> = ({ parentBreadcrumb, defaultValues }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { openInstallationSaveDialog } = useModals()
  const messages = useMemo(() => getValidationMessages(t), [t])
  const schema = useMemo(() => installationSchema(t), [t])
  const { triggerAppRating } = useAppRatingContext()

  const methods = useForm<InstallationFormModel>({
    defaultValues,
    mode: 'onChange',
    resetOptions: {
      keepDirty: false,
      keepDirtyValues: true,
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages,
      allowUnknown: true,
    }),
  })

  // Revalidate after language change as a macrotask, so it triggers after the translation change.
  // This is used to translate the errors after the new translations have loaded into the useForm resolver.
  const { trigger } = methods
  const language = getCurrentLanguage()
  useEffect(() => {
    const id = setTimeout(() => trigger(), 0)
    return () => clearTimeout(id)
  }, [language, trigger])

  // Revalidate when HTC320H is treated as SG-Ready or not.
  const { generalSettings, heatpump, ioInterfaces } = methods.getValues()

  const treatHtcAsNonSgReady = getTreatHtcAsNonSgReady(generalSettings, ioInterfaces.storage, heatpump)
  useEffect(() => {
    trigger('ioInterfaces.htc320h')
  }, [trigger, treatHtcAsNonSgReady])

  const onSubmit: SubmitHandler<InstallationFormModel> = data => {
    openInstallationSaveDialog({
      requests: getInstallationRequests(data, methods.formState, t, authFetch),
      reset: methods.reset,
      shouldSync: !!data.emcDevice?.pairingId,
    })
  }

  return (
    <FormProvider {...methods}>
      <Box
        component={'form'}
        onSubmit={e => {
          methods.handleSubmit(onSubmit)(e)
          triggerAppRating()
        }}
        onKeyDown={e => {
          // Prevent submit from happening when 'Enter' is pressed
          // https://github.com/orgs/react-hook-form/discussions/2549
          if (e.key === 'Enter') e.preventDefault()
        }}
      >
        <InstallationFormRoutes parentBreadcrumb={parentBreadcrumb} />
        <InstallationFormFooter />
      </Box>
    </FormProvider>
  )
}
